import {
  CreateIcon,
  DeliverIcon,
  GeneralSupportIcon,
  PlanIcon,
  ScaleIcon,
  SpringBoardIconProps,
} from '@/shared/ui/GeneralIcons';
import PlanPoster from '@/shared/assets/media/springboard/plan.webp';
import CreatePoster from '@/shared/assets/media/springboard/create.webp';
import ScalePoster from '@/shared/assets/media/springboard/scale.webp';
import DeliverPoster from '@/shared/assets/media/springboard/deliver.webp';
import PlanVideo from '@/shared/assets/media/springboard/plan.mp4';
import CreateVideo from '@/shared/assets/media/springboard/create.mp4';
import ScaleVideo from '@/shared/assets/media/springboard/scale.mp4';
import DeliverVideo from '@/shared/assets/media/springboard/deliver.mp4';
import { CATEGORY_COLORS, CategoryId } from '@/shared/constants/category-colors';
import { i18n } from '@/shared/i18n';

/* eslint-disable i18next/no-literal-string -- tab identifier values */
export const SPRINGBOARD_TAB_IDS = {
  PLAN: 'plan',
  CREATE: 'create',
  SCALE: 'scale',
  DELIVER: 'deliver',
  SUPPORT: 'support',
} as const;
/* eslint-enable i18next/no-literal-string */

export interface SpringBoardPage {
  id: CategoryId;
  label: string;
  Icon: React.ComponentType<SpringBoardIconProps>;
  subtitle: string;
  description: string;
  video: string;
  poster: string;
  color: string;
  labelColor: string;
  videoOverlayColor?: string;
  videoRotation?: number;
  toolsTitle?: string;
}

// Getters, not values: this array is built while the module is evaluated, so resolving per
// read is what lets the copy follow a language change instead of freezing at import.
export const SPRINGBOARD_TABS: Array<SpringBoardPage> = [
  {
    id: SPRINGBOARD_TAB_IDS.PLAN,
    get label() {
      return i18n.t('springboard:springboard.tabs.plan');
    },
    Icon: PlanIcon,
    get subtitle() {
      return i18n.t('springboard:springboard.tabs.planSubtitle');
    },
    get description() {
      return i18n.t('springboard:springboard.tabs.planDescription');
    },
    video: PlanVideo,
    poster: PlanPoster,
    color: CATEGORY_COLORS.plan.background,
    labelColor: CATEGORY_COLORS.plan.text,
  },
  {
    id: SPRINGBOARD_TAB_IDS.CREATE,
    get label() {
      return i18n.t('springboard:springboard.tabs.create');
    },
    Icon: CreateIcon,
    get subtitle() {
      return i18n.t('springboard:springboard.tabs.createSubtitle');
    },
    get description() {
      return i18n.t('springboard:springboard.tabs.createDescription');
    },
    video: CreateVideo,
    poster: CreatePoster,
    color: CATEGORY_COLORS.create.background,
    labelColor: CATEGORY_COLORS.create.text,
  },
  {
    id: SPRINGBOARD_TAB_IDS.SCALE,
    get label() {
      return i18n.t('springboard:springboard.tabs.scale');
    },
    Icon: ScaleIcon,
    get subtitle() {
      return i18n.t('springboard:springboard.tabs.scaleSubtitle');
    },
    get description() {
      return i18n.t('springboard:springboard.tabs.scaleDescription');
    },
    video: ScaleVideo,
    poster: ScalePoster,
    color: CATEGORY_COLORS.scale.background,
    labelColor: CATEGORY_COLORS.scale.text,
  },
  {
    id: SPRINGBOARD_TAB_IDS.DELIVER,
    get label() {
      return i18n.t('springboard:springboard.tabs.deliver');
    },
    Icon: DeliverIcon,
    get subtitle() {
      return i18n.t('springboard:springboard.tabs.deliverSubtitle');
    },
    get description() {
      return i18n.t('springboard:springboard.tabs.deliverDescription');
    },
    video: DeliverVideo,
    poster: DeliverPoster,
    color: CATEGORY_COLORS.deliver.background,
    labelColor: CATEGORY_COLORS.deliver.text,
  },
  {
    id: SPRINGBOARD_TAB_IDS.SUPPORT,
    get label() {
      return i18n.t('springboard:springboard.tabs.support');
    },
    Icon: GeneralSupportIcon,
    get subtitle() {
      return i18n.t('springboard:springboard.tabs.supportSubtitle');
    },
    get description() {
      return i18n.t('springboard:springboard.tabs.supportDescription');
    },
    video: CreateVideo,
    poster: CreatePoster,
    videoOverlayColor: 'rgba(63, 254, 0, 0.25)',
    videoRotation: 180,
    get toolsTitle() {
      return i18n.t('springboard:springboard.tabs.supportToolsTitle');
    },
    color: CATEGORY_COLORS.support.background,
    labelColor: CATEGORY_COLORS.support.text,
  },
];

// Utility function to get a tab by id
export const getSpringboardTabById = (tabId: string): SpringBoardPage | undefined => {
  return SPRINGBOARD_TABS.find((tab) => tab.id === tabId);
};

// Utility function to get default tab
export const getDefaultSpringboardTab = (): SpringBoardPage => {
  return SPRINGBOARD_TABS[0];
};
